import {
  recoverToLatestBuildIfShellInstalled,
  type ShellRecoveryResult,
} from '@/lib/helpers/appUpdateRecovery'
import {
  canAttemptReload,
  recordReloadAttempt,
  verifyReloadOutcome,
} from '@/lib/helpers/buildReloadGuard'
import { parseDeployedBuild, type DeployedBuild } from '@/lib/helpers/versionCheckDecisions'
import { getPlatform } from '@/lib/platform/platformDetection'

/**
 * Web startup: is this tab about to mount a build other than the deployed one? (AUTHZ-02 E2,
 * decision 3.) Native never takes this path — its shell ships inside the app binary.
 *
 * The `version.json` probe starts BEFORE the persisted query cache is restored and runs in
 * parallel with it, raced against a deadline counted from the probe's start. A slow or dead link
 * therefore costs startup at most the deadline, and usually nothing, because the restore takes
 * time of its own.
 *
 * On a mismatch the tab reloads ONLY when a fresh shell was fetched and installed
 * (`recoverToLatestBuildIfShellInstalled`) and the loop guard allows one more reload towards that
 * build (buildReloadGuard.ts). Otherwise it mounts as before: useVersionCheck's mount check shows
 * the persistent update toast, and the reload is retried when the browser comes back `online` —
 * but only while the user has not started working in the mounted page (no pointer or key input
 * yet), because a reload after that is the forced reload decision 3 rules out on `online`.
 */

export const STARTUP_PROBE_DEADLINE_MS = 1500

/** The build id `vite serve` defines; a dev server has no deployed build to compare against. */
const DEV_BUILD_ID = 'dev'

const LOG_PREFIX = 'StartupBuildCheck'

export type GuardedReloadResult =
  | ShellRecoveryResult
  | { reloaded: false; reason: 'reload-guarded' }

/**
 * An AUTOMATIC reload towards `target`: allowed by the loop guard, recorded before it
 * navigates, and only when a fresh shell is installed. Manual reloads use recoverToLatestBuild.
 */
export async function reloadToDeployedBuild(
  target: string,
  logPrefix: string,
): Promise<GuardedReloadResult> {
  if (!canAttemptReload(target)) {
    console.warn(`[${logPrefix}] Not reloading towards ${target}: already tried recently`)
    return { reloaded: false, reason: 'reload-guarded' }
  }
  return recoverToLatestBuildIfShellInstalled({
    reloadParam: 'v',
    logPrefix,
    beforeReload: () => recordReloadAttempt(target),
  })
}

/** The deployed build, or `null` when it cannot be read right now (offline, 5xx, bad body). */
export async function fetchDeployedBuild(): Promise<DeployedBuild | null> {
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    return parseDeployedBuild(await response.json())
  } catch {
    return null
  }
}

export type StartupBuildCheckOptions = {
  buildId?: string
  isWeb?: boolean
  deadlineMs?: number
  fetchDeployed?: () => Promise<DeployedBuild | null>
  reload?: (target: string, logPrefix: string) => Promise<GuardedReloadResult>
}

export type StartupBuildCheck = {
  /** True when the page is navigating to the deployed build: do not mount it. */
  reloadIfStale: () => Promise<boolean>
  /** Stop the `online` retry (test seam; the page normally just goes away). */
  dispose: () => void
}

const TIMED_OUT = Symbol('timed-out')

/** Reasons worth another try once the link is back; a guarded target would be refused again. */
function retryOnOnline(result: GuardedReloadResult): boolean {
  return !result.reloaded && result.reason !== 'reload-guarded'
}

export function startStartupBuildCheck({
  buildId = __BUILD_ID__,
  isWeb = getPlatform() === 'web',
  deadlineMs = STARTUP_PROBE_DEADLINE_MS,
  fetchDeployed = fetchDeployedBuild,
  reload = reloadToDeployedBuild,
}: StartupBuildCheckOptions = {}): StartupBuildCheck {
  if (!isWeb || buildId === DEV_BUILD_ID) {
    return { reloadIfStale: async () => false, dispose: () => undefined }
  }

  // Did the reload the previous page started land where it was sent?
  const outcome = verifyReloadOutcome(buildId)
  if (outcome && !outcome.landed) {
    console.warn(
      `[${LOG_PREFIX}] The reload towards ${outcome.target} landed on ${buildId} ` +
        `(missed ${outcome.missedCount}x in this tab)`,
    )
  }

  const probe = fetchDeployed()
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<typeof TIMED_OUT>(resolve => {
    deadlineTimer = setTimeout(() => resolve(TIMED_OUT), deadlineMs)
  })

  let disposeRetry: () => void = () => undefined

  const armOnlineRetry = () => {
    let interacted = false
    let inFlight = false

    const onInteract = () => {
      interacted = true
      disposeRetry()
    }

    const onOnline = async () => {
      if (interacted || inFlight) return
      inFlight = true
      try {
        const deployed = await fetchDeployed()
        if (interacted || deployed === null) return
        if (deployed.version === buildId) {
          disposeRetry()
          return
        }
        const result = await reload(deployed.version, LOG_PREFIX)
        if (!retryOnOnline(result)) disposeRetry()
      } catch (error) {
        console.error(`[${LOG_PREFIX}] Online retry failed`, error)
      } finally {
        inFlight = false
      }
    }

    const listener = () => void onOnline()
    window.addEventListener('online', listener)
    window.addEventListener('pointerdown', onInteract, { capture: true, passive: true })
    window.addEventListener('keydown', onInteract, { capture: true, passive: true })

    disposeRetry = () => {
      window.removeEventListener('online', listener)
      window.removeEventListener('pointerdown', onInteract, { capture: true })
      window.removeEventListener('keydown', onInteract, { capture: true })
    }
  }

  const settle = async (): Promise<boolean> => {
    const deployed = await Promise.race([probe, deadline])
    clearTimeout(deadlineTimer)

    if (deployed === TIMED_OUT || deployed === null) {
      armOnlineRetry()
      return false
    }
    if (deployed.version === buildId) return false

    const result = await reload(deployed.version, LOG_PREFIX)
    if (result.reloaded) return true

    console.warn(
      `[${LOG_PREFIX}] Mounting ${buildId} although ${deployed.version} is deployed: ${result.reason}`,
    )
    if (retryOnOnline(result)) armOnlineRetry()
    return false
  }

  return {
    // Never throws: whatever goes wrong here, the app still mounts.
    reloadIfStale: () =>
      settle().catch(error => {
        console.error(`[${LOG_PREFIX}] Startup build check failed`, error)
        return false
      }),
    dispose: () => disposeRetry(),
  }
}
